/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import 'moment/locale/en-gb';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CheckMarkFormField from '../../../components/form/CheckMarkFormField';
import DateRangeInputFormField from '../../../components/form/DateRangeInputFormField';
import RadioValueFormField from '../../../components/form/RadioValueFormField';
import SelectSearchFormField from '../../../components/form/SelectSearchFormField';
import TagInputField from '../../../components/form/TagInputField';
import TextFormField from '../../../components/form/TextFormField';
import Tip from '../../../components/Tip/Tip';
import c from '../../../constant';
import useStore from '../../../store';
import { isUserPermitted } from '../../../utils/checker';
import { getDepartment, getDepartmentOffices, getDepartments, getOffice } from '../../../utils/departmentOfficeSelectionWrapper';

function Filter({
	step = '',
	type = '',
	number = '',
	subject = '',
	department = null,
	office = null,
	unread = false,
	isPrinted = null,
	isAssistantAssignment = null,
	tags = null,
	dgSignedDate = '',
	more = false,
	onCallback = () => null,
	onClose = () => null,
}) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				type: Yup.string().nullable(),
				number: Yup.string().nullable(),
				subject: Yup.string().nullable(),
				department: Yup.object().nullable(),
				office: Yup.object().nullable(),
				tags: Yup.array().of(Yup.string().required('required')).min(0),
				dgSignedDate: Yup.string().nullable(),
				unread: Yup.boolean(),
				isPrinted: Yup.boolean().nullable(),
				isAssistantAssignment: Yup.boolean().nullable(),
			}),
		[],
	);

	const isPrintingPermission = useMemo(() => {
		if (user) {
			const role = user?.role || '';
			const roles = role?.split(',');
			return roles?.includes('admin') || (roles?.includes('nprp') && isUserPermitted(['nprpPrintStatus'], [...(user?.nprp ?? [])]));
		}
		return false;
	}, [user]);

	const isAssistantPermission = useMemo(() => {
		if (user) {
			const roles = user?.role?.split(',') || [];
			return roles.includes('assistant') || roles.includes('admin');
		}
		return false;
	}, [user]);

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={true} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								type: type ?? '',
								number: number ?? '',
								subject: subject ?? '',
								department: getDepartment(department, lang),
								office: getOffice(department, office, lang),
								tags,
								dgSignedDate: dgSignedDate ?? '',
								unread,
								isPrinted,
								isAssistantAssignment,
							}}
							validationSchema={validationSchema}
							onSubmit={(values) => {
								let url = '?page=1';
								if (step) {
									url += '&step=' + step;
								}
								if (values?.type) {
									url += '&type=' + values?.type;
								}
								if (values?.number) {
									url += '&number=' + values?.number;
								}
								if (values?.subject) {
									url += '&subject=' + values?.subject;
								}
								if (values?.department?.value) {
									url += '&department=' + values?.department?.value;
								}
								if (values?.office?.value) {
									url += '&office=' + values?.office?.value;
								}
								if (values?.tags?.length > 0) {
									url += '&tags=' + encodeURIComponent(JSON.stringify(values?.tags));
								}
								if (values?.dgSignedDate && values?.dgSignedDate?.split(',')?.filter(Boolean)?.length === 2) {
									url += '&dgSignedDate=' + values?.dgSignedDate;
								}
								if (values?.unread) {
									url += '&unread=' + values?.unread;
								}
								if (values?.isPrinted === true) {
									url += '&isPrinted=' + values?.isPrinted;
								}
								if (values?.isAssistantAssignment === true) {
									url += '&isAssistantAssignment=' + values?.isAssistantAssignment;
								}
								if (more) {
									url += '&more=' + more;
								}
								onCallback(url);
								onClose();
							}}
						>
							{({ values, ...props }) => {
								return (
									<Box pb="4">
										<Form>
											<RadioValueFormField
												name="type"
												label="type"
												disabled={false}
												required={false}
												radios={[
													{ label: 'Note of Presentation', value: 'NoteOfPresentation' },
													{ label: 'Report', value: 'Report' },
												]}
											/>
											<TextFormField {...props} label="Number" name="number" />
											<TextFormField {...props} label="Subject" name="subject" />
											<SelectSearchFormField
												{...props}
												name="department"
												label="Department"
												placeholder="Department"
												options={getDepartments(lang)}
												callback={(e) => props?.setFieldValue('office', null)}
											/>
											{values?.department && (
												<>
													<SelectSearchFormField
														{...props}
														name="office"
														label="Office"
														placeholder="Office"
														options={getDepartmentOffices(values?.department?.value, lang)}
													/>
												</>
											)}
											<TagInputField name="tags" label="Tag" />
											<Box marginTop={4} marginBottom={4}>
												<center>
													<DateRangeInputFormField
														lang={lang}
														label="Director General Signed Date"
														name="dgSignedDate"
														tip={
															<Tip
																childDescription={
																	<Text fontSize="sm" fontWeight="400" color="blue.500">
																		ច្រោះយក&nbsp;<span style={{ fontWeight: 'bold' }}>កំណត់បង្ហាញរឿង/របាយការណ៍</span>
																		&nbsp;ដែលអគ្គនាយកបានចុះហត្ថលេខាក្នុងជួរកាលបរិច្ឆេទជ្រើសរើស
																	</Text>
																}
															/>
														}
													/>
												</center>
											</Box>
											<>
												<CheckMarkFormField {...props} label="Show unread only" name="unread" />
											</>
											{isPrintingPermission && (
												<>
													<CheckMarkFormField {...props} label="Printed as hard copy" name="isPrinted" />
												</>
											)}
											{isAssistantPermission && (
												<>
													<CheckMarkFormField {...props} label="Assistant Assignment" name="isAssistantAssignment" />
												</>
											)}
											<Flex mt="32px" justifyContent="flex-end">
												<Button
													variant="ghost"
													size="md"
													h="42px"
													w="128px"
													onClick={() => {
														let url = '?page=1';
														if (step) {
															url += '&step=' + step;
														}
														if (more) {
															url += '&more=' + more;
														}
														onCallback(url);
														onClose();
													}}
												>
													{t('Clear')}
												</Button>
												<Button
													ml={4}
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													type="submit"
													borderRadius={c.borderRadius}
													color="#3498db"
												>
													{t('Apply')}
												</Button>
											</Flex>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default Filter;
